import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid, useMediaQuery, useTheme } from '@mui/material'

import {
  LayoutFormat,
  LayoutButtons,
  Filter,
  PaginationButtons,
  PaginationContext,
  AccountsGrid,
} from 'src/components'
import { SubscriptionCount } from './subscription-count.component'
import { AccountsTable } from '../accounts-table'
import {
  usePagination,
  useAllAccounts,
  initialAccountsAllFilterValues,
  useSubscriptionsSelectionOptions,
  useFilter,
  useAIGeneratedEntitySelectionOptions,
} from 'src/hooks'

import { countOffset, sortToObject } from 'src/common/utils'
import {
  defaultPage,
  defaultAccountsLimit,
  defaultSort,
  accountsAllSortOptions,
} from './filter/sort.filter'
import { useSusepnedListSelectionOptions } from './use-all-suspended-accounts.hook'
import { useUserListSelectionOptions } from './use-all-white-labels.hook'
import { useCssListSelectionOptions } from './use-account-white-labels.hook'

const AccountAllList = () => {
  const [t] = useTranslation()
  const [filter, setFilter] = useFilter({ initialState: initialAccountsAllFilterValues })
  const [sort, setSort] = useState(defaultSort)
  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination(
    defaultPage,
    defaultAccountsLimit,
  )
  const theme = useTheme()
  const isLaptop = useMediaQuery(theme.breakpoints.down('laptop_l'))
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  const [layoutFormat, setLayoutFormat] = useState<LayoutFormat>(
    isLaptop ? LayoutFormat.Grid : LayoutFormat.Table,
  )
  const { data: accounts, refetch: refetchAccounts } = useAllAccounts({
    ...filter,
    offset: countOffset(page, rowsPerPage),
    limit: rowsPerPage,
    ...sortToObject(sort),
  })

  useEffect(() => {
    refetchAccounts()
  }, [filter, sort, page, rowsPerPage, refetchAccounts])

  useEffect(() => {
    setPage(defaultPage)
  }, [filter, setPage, sort])

  const handleLayoutChange = () => setLayoutFormat(layoutFormat > 0 ? 0 : 1)

  const paginationValues = {
    count: accounts?.count ?? 0,
    page: page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    isShown: !!accounts && accounts.count > rowsPerPage,
  }

  const allSuspended = useSusepnedListSelectionOptions()
  const allWlp = useUserListSelectionOptions()
  const allCustomCss = useCssListSelectionOptions()
  const aiGeneratedEntitySelectionOptions = useAIGeneratedEntitySelectionOptions()
  const subscriptionsSelectionOptions = useSubscriptionsSelectionOptions()

  return (
    <Grid container>
      <>
        <Filter
          sort={sort}
          setSort={setSort}
          searchFieldText={t('accounts-search-input-placeholder.v')}
          filter={filter}
          setFilter={setFilter}
          selectionOptions={[
            { ...subscriptionsSelectionOptions, withSearch: true },
            { ...allWlp, withSearch: true },
            allSuspended,
            allCustomCss,
            aiGeneratedEntitySelectionOptions,
          ]}
          sortOptions={accountsAllSortOptions}
          initialState={initialAccountsAllFilterValues}
        />
        <SubscriptionCount
          total={accounts?.count}
          filter={filter}
          setFilter={setFilter}
          free={accounts?.freeCount}
          plus={accounts?.plusCount}
          premium={accounts?.premiumCount}
          enterprise={accounts?.enterpriseCount}
        />
        <LayoutButtons layoutFormat={layoutFormat} handleLayoutChange={handleLayoutChange} />
        <PaginationContext.Provider value={paginationValues}>
          {layoutFormat === LayoutFormat.Table ? (
            <AccountsTable sort={sort} setSort={setSort} data={accounts?.data || []} />
          ) : (
            <AccountsGrid data={accounts?.data || []} withSuspension />
          )}
          {!isTablet && <PaginationButtons />}
        </PaginationContext.Provider>
      </>
    </Grid>
  )
}

export { AccountAllList }
